import type { CslItem } from "../../core/csl-json/types.js";
import type { ILibrary } from "../../core/library-interface.js";
import type { CheckFinding } from "./types.js";

export type FixActionType =
  | "add_retracted_tag"
  | "add_retraction_note"
  | "remove_from_library"
  | "update_from_published"
  | "add_published_and_supersede"
  | "add_version_tag"
  | "add_concern_tag"
  | "add_concern_note"
  | "update_all_fields"
  | "update_selected_fields"
  | "skip";

export interface FixAction {
  type: FixActionType;
  label: string;
}

export interface FixActionResult {
  applied: boolean;
  message: string;
  removed?: boolean;
}

export function getFixActionsForFinding(finding: CheckFinding): FixAction[] {
  switch (finding.type) {
    case "retracted":
      return [
        { type: "add_retracted_tag", label: 'Add tag "retracted"' },
        { type: "add_retraction_note", label: "Add note with retraction details" },
        { type: "remove_from_library", label: "Remove from library" },
        { type: "skip", label: "Skip" },
      ];
    case "version_changed":
      return [
        // Listed first: overwriting in place changes what the existing citation key resolves
        // to, which breaks a manuscript already citing the preprint. Adding the published
        // version alongside and forwarding the old record keeps both working (#108).
        {
          type: "add_published_and_supersede",
          label: "Add published version as a new record, supersede this one",
        },
        { type: "update_from_published", label: "Update metadata from published version" },
        { type: "add_version_tag", label: 'Add tag "has-published-version"' },
        { type: "skip", label: "Skip" },
      ];
    case "concern":
      return [
        { type: "add_concern_tag", label: 'Add tag "expression-of-concern"' },
        { type: "add_concern_note", label: "Add note with concern details" },
        { type: "skip", label: "Skip" },
      ];
    case "metadata_mismatch":
    case "metadata_outdated":
      return [
        { type: "update_all_fields", label: "Update all changed fields from remote" },
        { type: "skip", label: "Skip" },
      ];
    default:
      return [];
  }
}

function addTag(item: CslItem, tag: string): string[] {
  const existing = (item.custom?.tags as string[] | undefined) ?? [];
  if (existing.includes(tag)) {
    return existing;
  }
  return [...existing, tag];
}

function buildNoteText(prefix: string, finding: CheckFinding): string {
  const parts = [prefix];
  if (finding.details?.retractionDate) {
    parts.push(`Date: ${finding.details.retractionDate}`);
  }
  if (finding.details?.retractionDoi) {
    parts.push(`DOI: ${finding.details.retractionDoi}`);
  }
  return parts.join(". ");
}

function appendNote(existingNote: string | undefined, newNote: string): string {
  if (existingNote) {
    return `${existingNote}\n\n${newNote}`;
  }
  return newNote;
}

async function applyTagAction(
  library: ILibrary,
  item: CslItem,
  tag: string
): Promise<FixActionResult> {
  const tags = addTag(item, tag);
  await library.update(item.id, { custom: { ...item.custom, tags } } as Partial<CslItem>, {
    idType: "id",
  });
  await library.save();
  return { applied: true, message: `Added tag "${tag}"` };
}

async function applyNoteAction(
  library: ILibrary,
  item: CslItem,
  prefix: string,
  finding: CheckFinding
): Promise<FixActionResult> {
  const noteText = buildNoteText(prefix, finding);
  const note = appendNote(item.note, noteText);
  await library.update(item.id, { note } as Partial<CslItem>, { idType: "id" });
  await library.save();
  return { applied: true, message: `Added note: ${noteText}` };
}

async function applyUpdateFromPublished(
  library: ILibrary,
  item: CslItem,
  finding: CheckFinding
): Promise<FixActionResult> {
  const newDoi = finding.details?.newDoi;
  if (!newDoi) {
    return { applied: false, message: "No published DOI available in finding details" };
  }
  const { fetchDoi } = await import("../import/fetcher.js");
  const fetchResult = await fetchDoi(newDoi);
  if (!fetchResult.success) {
    return {
      applied: false,
      message: `Failed to fetch metadata for ${newDoi}: ${fetchResult.error}`,
    };
  }
  const { id: _id, custom: _custom, ...metadata } = fetchResult.item;
  await library.update(item.id, metadata as Partial<CslItem>, { idType: "id" });
  await library.save();
  return { applied: true, message: `Updated metadata from ${newDoi}` };
}

/**
 * Add the published version as its own record and point the old one at it.
 *
 * The alternative action overwrites the existing record in place, which silently changes what
 * the preprint's citation key resolves to. This keeps both records: the manuscript citing the
 * preprint still resolves, and every read command reports where to cite instead (#108).
 */
async function applyAddPublishedAndSupersede(
  library: ILibrary,
  item: CslItem,
  finding: CheckFinding
): Promise<FixActionResult> {
  const newDoi = finding.details?.newDoi;
  if (!newDoi) {
    return { applied: false, message: "No published DOI available in finding details" };
  }

  // The published version may already have been imported separately — reuse it rather than
  // adding a second copy, which would just create the duplicate this feature exists to resolve.
  let published = await library.find(newDoi, { idType: "doi" });

  if (!published) {
    const { fetchDoi } = await import("../import/fetcher.js");
    const fetchResult = await fetchDoi(newDoi);
    if (!fetchResult.success) {
      return {
        applied: false,
        message: `Failed to fetch metadata for ${newDoi}: ${fetchResult.error}`,
      };
    }
    // Drop the fetched id and custom block: Library.add generates a citation key that cannot
    // collide, and a uuid the superseded pointer can target.
    const { id: _id, custom: _custom, ...metadata } = fetchResult.item;
    published = await library.add(metadata as CslItem);
  }

  const { deprecateReference } = await import("../operations/deprecate.js");
  const outcome = await deprecateReference(library, {
    identifier: item.id,
    target: published.id,
    reason: "published_version",
  });

  if (!outcome.applied) {
    // The new record is already in the library, so say so rather than implying nothing happened
    await library.save();
    return {
      applied: false,
      message: `Added ${published.id}, but could not mark ${item.id} as superseded (${outcome.errorType ?? "unknown"})`,
    };
  }

  return {
    applied: true,
    message: `Added ${published.id} and marked ${item.id} as superseded by it`,
  };
}

async function applyUpdateAllFields(
  library: ILibrary,
  item: CslItem,
  finding: CheckFinding
): Promise<FixActionResult> {
  let fetchedItem: CslItem;

  if (item.DOI) {
    const { fetchDoi } = await import("../import/fetcher.js");
    const fetchResult = await fetchDoi(item.DOI);
    if (!fetchResult.success) {
      return {
        applied: false,
        message: `Failed to fetch metadata for DOI ${item.DOI}: ${fetchResult.error}`,
      };
    }
    fetchedItem = fetchResult.item;
  } else if (item.PMID) {
    const { fetchPmids } = await import("../import/fetcher.js");
    const results = await fetchPmids([item.PMID], {});
    const result = results[0];
    if (!result || !result.success) {
      return {
        applied: false,
        message: `Failed to fetch metadata for PMID ${item.PMID}: ${result?.error ?? "unknown error"}`,
      };
    }
    fetchedItem = result.item;
  } else {
    return { applied: false, message: "No DOI or PMID available for metadata update" };
  }

  const updatedFields = finding.details?.updatedFields ?? [];
  const updates: Partial<CslItem> = {};
  for (const field of updatedFields) {
    if (field in fetchedItem) {
      (updates as Record<string, unknown>)[field] = (fetchedItem as Record<string, unknown>)[field];
    }
  }
  if (Object.keys(updates).length === 0) {
    return { applied: false, message: "No fields to update" };
  }
  await library.update(item.id, updates, { idType: "id" });
  await library.save();
  return { applied: true, message: `Updated fields: ${updatedFields.join(", ")}` };
}

export async function applyFixAction(
  library: ILibrary,
  item: CslItem,
  finding: CheckFinding,
  actionType: FixActionType
): Promise<FixActionResult> {
  switch (actionType) {
    case "add_retracted_tag":
      return applyTagAction(library, item, "retracted");

    case "add_retraction_note":
      return applyNoteAction(library, item, "RETRACTED", finding);

    case "add_concern_tag":
      return applyTagAction(library, item, "expression-of-concern");

    case "add_concern_note":
      return applyNoteAction(library, item, "EXPRESSION OF CONCERN", finding);

    case "add_version_tag":
      return applyTagAction(library, item, "has-published-version");

    case "remove_from_library": {
      const removeResult = await library.remove(item.id, { idType: "id" });
      if (!removeResult.removed) {
        return { applied: false, message: `Failed to remove ${item.id}` };
      }
      await library.save();
      return { applied: true, message: `Removed ${item.id}`, removed: true };
    }

    case "update_from_published":
      return applyUpdateFromPublished(library, item, finding);

    case "add_published_and_supersede":
      return applyAddPublishedAndSupersede(library, item, finding);

    case "update_all_fields":
      return applyUpdateAllFields(library, item, finding);

    // Placeholder for programmatic API (MCP/server) use.
    // Individual field selection is not available in interactive CLI mode,
    // but can be used via the MCP tool or HTTP server API.
    case "update_selected_fields":
      return { applied: false, message: "Field selection not available in CLI mode" };

    case "skip":
      return { applied: true, message: "Skipped" };

    default:
      return { applied: false, message: `Unknown action: ${actionType}` };
  }
}
